// filepath: c:\Users\ejiro\MEDFILES\Transform\frontend\src\utils\tenantCache.js
/**
 * Tenant Cache Utility
 * Provides a memory cache for tenant data to prevent excessive API calls and UI flickering
 */

// Cache structure with TTL and deduplication mechanisms
const cache = {
  // Tenant data cache with TTL
  tenantData: null,
  lastFetched: 0,
  fetchPromise: null,
  
  // Tenant ID cache
  tenantId: null,
  tenantIdLastValidated: 0,
  
  // Settings for cache behavior
  TTL: 300000, // 5 minutes default TTL
  TENANT_ID_TTL: 600000, // 10 minutes for tenant ID validation
  
  // Request throttling
  lastRequestTime: 0,
  minimumRequestInterval: 2000, // 2 seconds between requests
};

/**
 * Store tenant data in the cache
 * @param {Object} data - The tenant data to cache
 * @param {number} ttl - Optional TTL override in milliseconds
 */
export const cacheTenantData = (data, ttl = cache.TTL) => {
  if (!data) return;
  
  cache.tenantData = data;
  cache.lastFetched = Date.now();
  
  // Also update tenant ID cache if ID is in the data
  if (data._id || data.id) {
    const id = data._id || data.id;
    cache.tenantId = id;
    cache.tenantIdLastValidated = Date.now();
    
    // Store in localStorage for persistence only if it's changed
    const storedId = localStorage.getItem('tenantId');
    if (storedId !== id) {
      localStorage.setItem('tenantId', id);
    }
  }
  
  // Log cache update with expiry time
  console.log(`Tenant cache updated. Will expire at ${new Date(cache.lastFetched + ttl).toLocaleTimeString()}`);
};

/**
 * Get tenant data from cache if available and not expired
 * @param {boolean} allowExpired - Whether to return expired data
 * @returns {Object|null} - Cached tenant data or null if expired/unavailable
 */
export const getCachedTenantData = (allowExpired = false) => {
  const now = Date.now();
  
  if (!cache.tenantData) return null;
  
  // Return cached data if within TTL or if we allow expired data
  if (allowExpired || (now - cache.lastFetched < cache.TTL)) {
    return cache.tenantData;
  }
  
  // Data is expired
  return null;
};

/**
 * Check if a tenant API request should be throttled
 * @returns {boolean} - True if the request should be throttled
 */
export const shouldThrottleRequest = () => {
  const now = Date.now();
  const timeSinceLastRequest = now - cache.lastRequestTime;
  
  // Allow the request if enough time has passed
  if (timeSinceLastRequest >= cache.minimumRequestInterval) {
    cache.lastRequestTime = now;
    return false;
  }
  
  // Throttle the request
  return true;
};

/**
 * Cache and validate a tenant ID
 * @param {string} id - The tenant ID to cache
 */
export const cacheTenantId = (id) => {
  if (!id) return;
  
  cache.tenantId = id;
  cache.tenantIdLastValidated = Date.now();
  
  // Store in localStorage for persistence only if it's changed
  const storedId = localStorage.getItem('tenantId');
  if (storedId !== id) {
    localStorage.setItem('tenantId', id);
  }
};

/**
 * Get a cached tenant ID if available and not expired
 * @param {boolean} allowExpired - Whether to return expired IDs
 * @returns {string|null} - Cached tenant ID or null
 */
export const getCachedTenantId = (allowExpired = true) => {
  const now = Date.now();
  
  if (!cache.tenantId) return null;
  
  // Return cached ID if within TTL or if we allow expired IDs
  if (allowExpired || (now - cache.tenantIdLastValidated < cache.TENANT_ID_TTL)) {
    return cache.tenantId;
  }
  
  // ID validation is expired
  return cache.tenantId; // Still return the ID even if expired for tenant ID
};

/**
 * Store an in-progress fetch promise to deduplicate requests
 * @param {Promise} promise - The fetch promise
 */
export const setFetchPromise = (promise) => {
  cache.fetchPromise = promise;
};

/**
 * Get the current fetch promise if one exists
 * @returns {Promise|null} - The current fetch promise or null
 */
export const getFetchPromise = () => {
  return cache.fetchPromise;
};

/**
 * Clear the fetch promise after it resolves/rejects
 */
export const clearFetchPromise = () => {
  cache.fetchPromise = null;
};

/**
 * Reset the cache completely (for logout)
 */
export const resetCache = () => {
  cache.tenantData = null;
  cache.lastFetched = 0;
  cache.fetchPromise = null;
  // Don't clear tenant ID on logout to maintain tenant context
};

export default {
  cacheTenantData,
  getCachedTenantData,
  cacheTenantId,
  getCachedTenantId,
  setFetchPromise,
  getFetchPromise,
  clearFetchPromise,
  resetCache,
  shouldThrottleRequest
};